/*
 * UNSUBSCRIBE (decision 621): the page a summary's unsubscribe link opens.
 *
 * A BUTTON, NOT ON LOAD, and that is the opposite of the verification page on
 * purpose. Verifying is what the reader came to do and opening the link is the
 * act; here the link sits at the foot of every summary, where a mail scanner,
 * a stray tap or a forwarded message can open it, and turning off somebody's
 * email because a preview fetched the page would be the wrong failure. Mail
 * clients that support one-click do it themselves, with the RFC 8058 POST, and
 * never come here.
 *
 * Works signed in or out: the token is the credential. Turning summaries off
 * changes nothing else -- the categories and the weekday are kept, so turning
 * them back on in Settings restores what the reader had.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Brand } from "../components/Brand";
import { ApiError, api } from "../lib/api";
import { useLinkToken } from "../lib/linkToken";
import styles from "./SignIn.module.css";

export function Unsubscribe({ signedIn }: { signedIn: boolean }) {
  const { t } = useTranslation();
  const token = useLinkToken();
  const [state, setState] = useState<"ready" | "working" | "done" | "invalid" | "failed">(
    token === null ? "invalid" : "ready",
  );

  async function turnOff() {
    if (!token) return;
    setState("working");
    try {
      await api.unsubscribeEmailSummary(token);
      setState("done");
    } catch (err) {
      setState(
        err instanceof ApiError && err.failure.kind === "server" && err.failure.status === 400
          ? "invalid"
          : "failed",
      );
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <Brand variant="lockup" height={34} />
        </div>

        <h1 className={styles.heading}>{t("unsubscribe.heading")}</h1>

        {state === "done" ? (
          <>
            <p className={styles.done} role="status">
              {t("unsubscribe.done")}
            </p>
            <p className={styles.intro}>{t("unsubscribe.doneNote")}</p>
          </>
        ) : state === "invalid" ? (
          <p className={styles.intro}>{t("unsubscribe.invalid")}</p>
        ) : (
          <>
            <p className={styles.intro}>{t("unsubscribe.intro")}</p>
            {state === "failed" ? (
              <p className={styles.error} role="alert">
                {t("auth.errorGeneric")}
              </p>
            ) : null}
            <button
              className={styles.primary}
              type="button"
              disabled={state === "working"}
              onClick={() => void turnOff()}
            >
              {t("unsubscribe.button")}
            </button>
          </>
        )}

        <p className={styles.alt}>
          {signedIn ? (
            <Link to="/settings">{t("unsubscribe.toSettings")}</Link>
          ) : (
            <Link to="/">{t("auth.backToSignIn")}</Link>
          )}
        </p>
      </div>
    </div>
  );
}
